import React from 'react';
import { moduleApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import {
    BillingSettings,
    CatalogItem,
    CRMContactSuggestion,
    InvoiceLine,
    InvoiceLineSummary,
    DEFAULT_ACCENT_COLOR,
    buildEmptyLine,
    computeDefaultInvoiceNumber,
    createLineId,
    extractLogoAccentColor,
    getCRMDisplayName,
    hexToRgba,
    normalizeLineForRates,
    parseInitialInvoice,
    toInputDate,
} from './invoiceTypes';

/**
 * La facture en cours de saisie : ses champs, son chargement, ses lignes et tous les totaux qui
 * s'en déduisent.
 *
 * Extrait de CreateInvoice.tsx, qui faisait 2017 lignes : l'écran ne garde plus que les actions
 * (imprimer, télécharger, envoyer) et l'assemblage. Aucun calcul n'a changé.
 */
export function useInvoiceEditor(initialInvoice: any) {
    const { user } = useAuth();

    const [settings, setSettings] = React.useState<BillingSettings>({});
    const [catalog, setCatalog] = React.useState<CatalogItem[]>([]);
    const [billableItems, setBillableItems] = React.useState<{ id: string; source: 'commande' | 'extra'; label: string; amount: number }[]>([]);

    const [invoiceNumber, setInvoiceNumber] = React.useState('');
    const [invoiceDate, setInvoiceDate] = React.useState(toInputDate());

    const [clientName, setClientName] = React.useState('');
    const [clientAddress, setClientAddress] = React.useState('');
    const [clientCity, setClientCity] = React.useState('');
    const [clientPostalCode, setClientPostalCode] = React.useState('');
    const [clientCountry, setClientCountry] = React.useState('France');
    const [coverCount, setCoverCount] = React.useState('');
    const [amountAlreadyPaid, setAmountAlreadyPaid] = React.useState('');
    const [amountMode, setAmountMode] = React.useState<'ttc' | 'ht'>('ttc');
    const [currentInvoiceId, setCurrentInvoiceId] = React.useState('');
    const [selectedCrmContactId, setSelectedCrmContactId] = React.useState('');
    // Identité fiscale de l'acheteur, reprise de la fiche CRM au moment où on la choisit puis figée sur
    // la facture (EN 16931 BT-47/BT-48). Sur une facture entre professionnels, le SIRET et le numéro de
    // TVA du client sont des mentions obligatoires ; une facture déjà émise ne doit jamais changer parce
    // que la fiche CRM a été corrigée depuis.
    const [clientType, setClientType] = React.useState('');
    const [clientSiret, setClientSiret] = React.useState('');
    const [clientVatNumber, setClientVatNumber] = React.useState('');

    // --- EN-TÊTE DE FACTURE, DONNÉES EXIGÉES PAR LA NORME EN 16931 (lot 3) --------------------------
    // Réservées aux établissements de type Prestation (décision d'Emeni, 2026-09-07) : ce sont eux qui
    // facturent des professionnels, seuls concernés par la facture électronique obligatoire. Pour un
    // restaurant, ces champs n'apparaissent pas du tout.
    //   - nature de l'opération : livraison de biens, prestation de services, ou les deux. Donnée
    //     structurelle attendue par les plateformes agréées, pas une mention à imprimer.
    //   - référence de bon de commande (BT-13) : souvent exigée par les grands comptes pour rapprocher la
    //     facture de leur commande ; sans elle, la facture peut rester impayée.
    //   - adresse de livraison (BG-15) : uniquement quand elle diffère de l'adresse de facturation.
    const isPrestation = (user as any)?.client_type === 'prestation';
    const [operationNature, setOperationNature] = React.useState('services');
    const [purchaseOrderRef, setPurchaseOrderRef] = React.useState('');
    const [deliveryAddress, setDeliveryAddress] = React.useState('');
    const [crmSuggestions, setCrmSuggestions] = React.useState<CRMContactSuggestion[]>([]);
    const [showCrmSuggestions, setShowCrmSuggestions] = React.useState(false);
    const [crmSearchLoading, setCrmSearchLoading] = React.useState(false);
    const [recipientEmail, setRecipientEmail] = React.useState('');
    const [accentColor, setAccentColor] = React.useState(DEFAULT_ACCENT_COLOR);
    const [dataLoaded, setDataLoaded] = React.useState(false);
    const skipNextCrmSearchRef = React.useRef(false);

    // Franchise en base (art. 293 B du CGI) : aucun taux de TVA n'est proposé, la facture est établie
    // en HT = TTC. Point d'entrée unique — tout le reste de l'écran (colonnes de taux, totaux, lignes)
    // se déduit de cette liste, il n'y a donc rien d'autre à conditionner.
    const isFranchiseTva = settings.tva_regime === 'franchise_293b';
    const tvaRates = React.useMemo(
        () => (isFranchiseTva ? [0] : (settings.tva_rates && settings.tva_rates.length > 0 ? settings.tva_rates : [20])),
        [settings.tva_rates, isFranchiseTva]
    );

    // Règlement attendu, choisi par l'établissement au moment de clôturer la prestation (ou hérité de la
    // formule souscrite). Vide pour une facture créée à la main depuis cet écran : le module Facture ne
    // demande pas cette information et n'affiche donc aucune coordonnée bancaire, comme avant.
    const [expectedPaymentMethod, setExpectedPaymentMethod] = React.useState('');
    const [lines, setLines] = React.useState<InvoiceLine[]>([]);

    const loadBillingData = React.useCallback(async () => {
        try {
            const [allFactures, billingSettings] = await Promise.all([
                moduleApi.getFactures(),
                moduleApi.getBillingSettings(),
            ]);
            const s = billingSettings || {};
            setInvoiceNumber(computeDefaultInvoiceNumber(
                Array.isArray(allFactures) ? allFactures : [],
                String(s.company_name || (user as any)?.companyName || ''),
                String(user?.clientId || user?.id || '')
            ));
            setSettings(s);

            const parsedCatalog = (() => {
                try {
                    const raw = JSON.parse(s.prestations_catalog || '[]');
                    if (!Array.isArray(raw)) return [];
                    return raw
                        .map((r: any, i: number) => ({
                            id: String(r.id || i + 1),
                            label: String(r.label || '').trim(),
                            unit_price_ttc: Number(r.unit_price_ttc ?? 0),
                            tax_rate: Number(r.tax_rate ?? 0),
                        }))
                        .filter((r: CatalogItem) => r.label.length > 0);
                } catch {
                    return [];
                }
            })();

            setCatalog(parsedCatalog);
            const nextRates = Array.isArray(s.tva_rates) && s.tva_rates.length > 0 ? s.tva_rates : [20];

            // Si on a un initialInvoice, on le traite ICI pour éviter les race conditions
            if (initialInvoice) {
                const parsed = parseInitialInvoice(initialInvoice, nextRates, parsedCatalog[0]?.label || '');

                if (parsed.snapshot && Object.keys(parsed.snapshot).length > 0) {
                    setSettings((prev) => ({ ...prev, ...parsed.snapshot }));
                }

                const v = parsed.values;
                setCurrentInvoiceId(v.currentInvoiceId);
                setExpectedPaymentMethod(v.expectedPaymentMethod);
                setInvoiceNumber(v.invoiceNumber);
                setInvoiceDate(v.invoiceDate);
                setClientName(v.clientName);
                setSelectedCrmContactId(v.selectedCrmContactId);
                setClientType(v.clientType);
                setClientSiret(v.clientSiret);
                setClientVatNumber(v.clientVatNumber);
                setOperationNature(v.operationNature);
                setPurchaseOrderRef(v.purchaseOrderRef);
                setDeliveryAddress(v.deliveryAddress);
                setClientAddress(v.clientAddress);
                setClientCity(v.clientCity);
                setClientPostalCode(v.clientPostalCode);
                setClientCountry(v.clientCountry);
                setRecipientEmail(v.recipientEmail);
                setCoverCount(v.coverCount);
                setAmountAlreadyPaid(v.amountAlreadyPaid);
                setAmountMode(parsed.amountMode);
                setLines(parsed.lines);
            } else {
                setLines((prev) => {
                    const normalized = prev.length > 0 ? prev.map((line) => normalizeLineForRates(line, nextRates)) : [buildEmptyLine(nextRates)];
                    if (parsedCatalog[0] && normalized[0] && !normalized[0].label.trim()) {
                        normalized[0] = { ...normalized[0], label: parsedCatalog[0].label };
                    }
                    return normalized;
                });
            }

            setDataLoaded(true);
        } catch (e) {
            console.error(e);
            setDataLoaded(true);
        }
    }, [user?.clientId, (user as any)?.companyName, user?.id, initialInvoice]);

    React.useEffect(() => {
        loadBillingData();
    }, [loadBillingData]);

    React.useEffect(() => {
        let active = true;
        extractLogoAccentColor(settings.logo_url)
            .then((color) => {
                if (active) setAccentColor(color || DEFAULT_ACCENT_COLOR);
            })
            .catch(() => {
                if (active) setAccentColor(DEFAULT_ACCENT_COLOR);
            });
        return () => {
            active = false;
        };
    }, [settings.logo_url]);

    const accentSoftBg = React.useMemo(() => hexToRgba(accentColor, 0.10), [accentColor]);
    const accentSoftBorder = React.useMemo(() => hexToRgba(accentColor, 0.25), [accentColor]);

    React.useEffect(() => {
        setLines((prev) => {
            if (prev.length === 0) return [buildEmptyLine(tvaRates, catalog[0]?.label || '')];
            return prev.map((line) => normalizeLineForRates(line, tvaRates));
        });
    }, [catalog, tvaRates]);

    const applyCRMContact = React.useCallback((contact: CRMContactSuggestion) => {
        skipNextCrmSearchRef.current = true;
        setSelectedCrmContactId(String(contact.id || ''));
        setClientName(getCRMDisplayName(contact));
        setClientAddress(String(contact.address || ''));
        setClientPostalCode(String(contact.postal_code || ''));
        setClientCity(String(contact.city || ''));
        setClientCountry(String(contact.country || 'France') || 'France');
        setRecipientEmail(String(contact.email || ''));
        setClientType(String((contact as any).type || ''));
        setClientSiret(String((contact as any).siret || ''));
        setClientVatNumber(String((contact as any).vat_number || ''));
        setShowCrmSuggestions(false);
        setCrmSuggestions([]);
    }, []);

    // Saisir un nom à la main détache la facture de la fiche CRM : l'identité fiscale reprise de cette
    // fiche ne vaut plus, il faut la reprendre à zéro.
    const handleClientNameChange = React.useCallback((value: string) => {
        setClientName(value);
        setSelectedCrmContactId('');
        setClientType('');
        setClientSiret('');
        setClientVatNumber('');
        setShowCrmSuggestions(true);
    }, []);

    React.useEffect(() => {
        const query = clientName.trim();
        if (skipNextCrmSearchRef.current) {
            skipNextCrmSearchRef.current = false;
            return;
        }

        if (query.length < 2) {
            setCrmSuggestions([]);
            return;
        }

        const timer = window.setTimeout(async () => {
            try {
                setCrmSearchLoading(true);
                const data = await moduleApi.searchFactureCRMContacts(query);
                setCrmSuggestions(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error(e);
                setCrmSuggestions([]);
            } finally {
                setCrmSearchLoading(false);
            }
        }, 180);

        return () => window.clearTimeout(timer);
    }, [clientName]);

    // Commandes fournisseur (montant réel) et extras (pas de prix, cf. décision produit du chantier
    // Extras — juste un libellé pré-rempli) rattachés au contact CRM de la facture, proposables comme
    // ligne de prestation. Un simple identifiant "commande:xxx"/"extra:xxx" évite toute confusion avec
    // le vrai catalogue de prestations si jamais deux libellés se ressemblent.
    React.useEffect(() => {
        if (!selectedCrmContactId) { setBillableItems([]); return; }
        let active = true;
        moduleApi.getBillableItems(selectedCrmContactId).then((res: any) => {
            if (active) setBillableItems(Array.isArray(res?.items) ? res.items : []);
        }).catch(() => { if (active) setBillableItems([]); });
        return () => { active = false; };
    }, [selectedCrmContactId]);

    const billableAsCatalog = React.useMemo<CatalogItem[]>(() => billableItems.map((item) => ({
        id: item.id,
        label: item.label,
        unit_price_ttc: item.amount,
        tax_rate: tvaRates[0],
    })), [billableItems, tvaRates]);

    const applyConfiguredUnitPrice = (label: string, quantity: number, currentTtcByRate: Record<number, number>) => {
        const selectedItem = [...catalog, ...billableAsCatalog].find((item) => item.label === label);
        const unitPriceTtc = Number(selectedItem?.unit_price_ttc ?? 0);
        if (!Number.isFinite(unitPriceTtc) || unitPriceTtc <= 0) {
            return currentTtcByRate;
        }
        const configuredRate = Number(selectedItem?.tax_rate ?? 0);
        const preferredRate = tvaRates?.includes(configuredRate)
            ? configuredRate
            : (tvaRates?.find((rate) => Number(currentTtcByRate?.[rate] ?? 0) > 0) ?? tvaRates?.[0]);
        return Object.fromEntries(
            (tvaRates || []).map((rate) => [rate, rate === preferredRate ? unitPriceTtc * quantity : 0])
        ) as Record<number, number>;
    };

    const updateLine = (lineId: string, patch: Partial<InvoiceLine>) => {
        setLines((prev) => prev.map((line) => {
            if (line.id !== lineId) return line;
            const nextLine = { ...line, ...patch };
            const nextLabel = patch.label ?? line.label;
            const nextQuantity = patch.quantity ?? line.quantity;
            return {
                ...nextLine,
                ttcByRate: applyConfiguredUnitPrice(nextLabel, nextQuantity, nextLine.ttcByRate),
            };
        }));
    };

    const updateLineRate = (lineId: string, rate: number, value: string) => {
        const numericValue = Math.max(0, Number(value || 0));
        setLines((prev) => prev.map((line) => line.id === lineId
            ? {
                ...line,
                ttcByRate: {
                    ...line.ttcByRate,
                    [rate]: amountMode === 'ttc' ? numericValue : numericValue * (1 + rate / 100),
                }
            }
            : line));
    };

    const addLine = () => {
        const defaultLabel = catalog[0]?.label || '';
        const newLine = buildEmptyLine(tvaRates, defaultLabel);
        newLine.ttcByRate = applyConfiguredUnitPrice(defaultLabel, newLine.quantity, newLine.ttcByRate);
        setLines((prev) => [...prev, newLine]);
    };

    const removeLine = (lineId: string) => {
        setLines((prev) => {
            const next = prev.filter((line) => line.id !== lineId);
            if (next.length > 0) return next;
            const defaultLabel = catalog[0]?.label || '';
            const fallbackLine = buildEmptyLine(tvaRates, defaultLabel);
            fallbackLine.ttcByRate = applyConfiguredUnitPrice(defaultLabel, fallbackLine.quantity, fallbackLine.ttcByRate);
            return [fallbackLine];
        });
    };

    const lineSummaries: InvoiceLineSummary[] = lines.map((line) => {
        const rateDetails = tvaRates.map((rate) => {
            const ttc = Number(line.ttcByRate?.[rate] ?? 0);
            const ht = ttc / (1 + rate / 100);
            const tva = ttc - ht;
            const sourceAmount = amountMode === 'ttc' ? ttc : ht;
            return { rate, ttc, ht, tva, sourceAmount };
        });

        return {
            ...line,
            rateDetails,
            totalHt: rateDetails.reduce((sum, detail) => sum + detail.ht, 0),
            totalTva: rateDetails.reduce((sum, detail) => sum + detail.tva, 0),
            totalTtc: rateDetails.reduce((sum, detail) => sum + detail.ttc, 0),
        };
    });

    const totalHt = lineSummaries.reduce((sum, line) => sum + line.totalHt, 0);
    const totalTva = lineSummaries.reduce((sum, line) => sum + line.totalTva, 0);
    const totalTtcBrut = lineSummaries.reduce((sum, line) => sum + line.totalTtc, 0);
    const alreadyPaidValue = Math.max(0, Number(amountAlreadyPaid || 0));
    const netToPay = Math.max(0, totalTtcBrut - alreadyPaidValue);
    const isSettled = totalTtcBrut > 0 && alreadyPaidValue >= totalTtcBrut;
    // Coordonnées bancaires : portées par la facture uniquement quand le règlement attendu est un
    // virement — c'est l'établissement qui l'a choisi, à la souscription de la formule ou à la clôture
    // de la prestation. En espèces ou par carte, l'IBAN n'a rien à faire sur la facture.
    // Groupé par 4 caractères, comme sur un relevé d'identité bancaire, pour se recopier sans erreur.
    const bankDetails = (expectedPaymentMethod === 'Virement' && settings.iban)
        ? `Paiement par virement — IBAN ${String(settings.iban).replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim()}${settings.bic ? ` • BIC ${settings.bic}` : ''}`
        : '';
    // Même libellé dynamique que le PDF téléchargé/renvoyé depuis l'historique (invoicePdfGenerator.ts)
    // et l'aperçu "Visualiser" (FacturesModule.tsx) — évite qu'un client reçoive un PDF qui affiche
    // toujours "Net à payer TTC" alors que l'établissement voit "Reste à payer" ailleurs.
    const bottomLabel = isSettled ? 'Total réglé' : (alreadyPaidValue > 0 ? 'Reste à payer' : 'Total à payer');
    const bottomColor = isSettled ? accentColor : (alreadyPaidValue > 0 ? '#dc2626' : accentColor);
    const previewHasContent = lineSummaries.some((line) => line.totalTtc > 0);
    const activeTvaRates = tvaRates.filter((rate) => lineSummaries.some((line) => line.rateDetails.some((detail) => detail.rate === rate && detail.ht > 0)));
    const displayTvaRates = activeTvaRates.length > 0 ? activeTvaRates : tvaRates;

    const buildInvoicePayload = React.useCallback(() => {
        const invoiceId = currentInvoiceId || (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : createLineId());
        return {
            id: invoiceId,
            invoiceNumber,
            invoiceDate,
            clientName,
            clientType,
            clientSiret,
            clientVatNumber,
            isPrestation,
            operationNature,
            purchaseOrderRef,
            deliveryAddress,
            currency: 'EUR',
            clientAddress,
            clientCity,
            clientPostalCode,
            clientCountry,
            crm_contact_id: selectedCrmContactId || null,
            recipientEmail: recipientEmail.trim(),
            coverCount,
            amountAlreadyPaid: alreadyPaidValue,
            amountMode,
            lines,
            totalHt,
            totalTva,
            totalTtcBrut,
            netToPay,
            expectedPaymentMethod,
            status: isSettled ? 'paid' : 'pending',
            billing_snapshot: settings,
        };
    }, [
        alreadyPaidValue, amountMode, clientAddress, clientCity, clientCountry, clientName,
        clientPostalCode, coverCount, currentInvoiceId, invoiceDate, invoiceNumber, isSettled,
        lines, netToPay, expectedPaymentMethod, recipientEmail, clientType, clientSiret,
        clientVatNumber, isPrestation, operationNature, purchaseOrderRef, deliveryAddress, selectedCrmContactId,
        settings, totalHt, totalTtcBrut, totalTva,
    ]);

    const resetAll = () => {
        setInvoiceDate(toInputDate());
        setClientName('');
        setSelectedCrmContactId('');
        setClientType('');
        setClientSiret('');
        setClientVatNumber('');
        setOperationNature('services');
        setPurchaseOrderRef('');
        setDeliveryAddress('');
        setCrmSuggestions([]);
        setClientAddress('');
        setClientCity('');
        setClientPostalCode('');
        setClientCountry('France');
        setRecipientEmail('');
        setCoverCount('');
        setAmountAlreadyPaid('');
        setCurrentInvoiceId('');
        setLines([buildEmptyLine(tvaRates, catalog[0]?.label || '')]);
        void loadBillingData();
    };

    /** Tout ce que le document de facture a besoin de savoir, dans un seul objet. */
    const documentProps = {
        settings, user, accentColor, accentSoftBg, accentSoftBorder,
        invoiceNumber, invoiceDate, coverCount,
        clientName, clientAddress, clientCity, clientPostalCode, clientCountry,
        clientType, clientSiret, clientVatNumber, deliveryAddress, purchaseOrderRef,
        lineSummaries, displayTvaRates, previewHasContent,
        totalHt, totalTva, totalTtcBrut, alreadyPaidValue, netToPay, isSettled, bottomLabel, bottomColor,
        isFranchiseTva, bankDetails,
        isPrestation, operationNature,
    };

    /** Tout ce que la colonne de saisie a besoin de savoir, dans un seul objet. */
    const formProps = {
        settings, user, isPrestation, clientType,
        invoiceNumber, setInvoiceNumber, invoiceDate, setInvoiceDate,
        operationNature, setOperationNature, purchaseOrderRef, setPurchaseOrderRef,
        deliveryAddress, setDeliveryAddress,
        clientName, onClientNameChange: handleClientNameChange, selectedCrmContactId, recipientEmail,
        showCrmSuggestions, setShowCrmSuggestions, crmSearchLoading, crmSuggestions, applyCRMContact,
        clientAddress, setClientAddress, clientCity, setClientCity,
        clientPostalCode, setClientPostalCode, clientCountry, setClientCountry,
        coverCount, setCoverCount,
        amountMode, setAmountMode, lines, lineSummaries, tvaRates, catalog, billableItems,
        addLine, removeLine, updateLine, updateLineRate,
        amountAlreadyPaid, setAmountAlreadyPaid,
    };

    return {
        user,
        settings,
        invoiceNumber,
        recipientEmail,
        setRecipientEmail,
        currentInvoiceId,
        setCurrentInvoiceId,
        dataLoaded,
        previewHasContent,
        buildInvoicePayload,
        resetAll,
        loadBillingData,
        documentProps,
        formProps,
    };
}
